import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'

import { PagedList, PriorExpense, PriorExpenseFilter, getPriorExpenses } from 'core'

import useQueryRequest from 'common/hooks/useQueryRequest'

import { Row, SimpleSearch, SimpleSelect, Skeleton } from 'common/components'

import PriorExpenseItem from './PriorExpenseItem'

interface ExpenseQueueProps {
  expenseId: string
  onPriorExpenseClick?: (recurringExpenseId: string) => void
}

const PriorExpenses: React.FC<ExpenseQueueProps> = (props) => {
  const { t: translate } = useTranslation()
  const parentScrollDiv = useRef(null)

  const [currentQueueNumber, setCurrentQueueNumber] = useState<number>(1)
  const [hasMoreQueueItems, setHasMoreQueueItems] = useState<boolean>(true)
  const [priorExpenseFilter, setPriorExpenseFilter] = useState<PriorExpenseFilter>({
    orderBy: '',
    orderDescending: false,
    searchValue: '',
    pageNumber: currentQueueNumber,
    pageSize: 10,
    currentExpenseId: props.expenseId,
  })

  const sortingOptions = [
    { key: 'VerifiedDate', value: 'Verified Date' },
    { key: 'Amount', value: 'Amount' },
    { key: 'ExpenseType', value: 'Expense Type' },
    { key: 'ProviderName', value: 'Provider Name' },
  ]

  const [, isPriorExpensesPagedLoading, sendGetPriorExpenses] = useQueryRequest(null)
  const [priorExpensesPaged, setPriorExpensesPaged] = useState<PagedList<PriorExpense> | null>(null)
  const [queueLoading, setQueueLoading] = useState<boolean>(false)

  useEffect(() => {
    ;(async () => {
      setQueueLoading(true)
      const result = await sendGetPriorExpenses(() => getPriorExpenses(priorExpenseFilter, 1))
      setCurrentQueueNumber(1)
      setPriorExpensesPaged(result)
      setHasMoreQueueItems(result.pageCount > 1)
      setQueueLoading(false)
    })()
  }, [props.expenseId, priorExpenseFilter])

  const onPriorExpensesEndReach = async () => {
    if (!priorExpensesPaged) return

    if (priorExpensesPaged.pageCount < currentQueueNumber) return

    const nextPageNumber = currentQueueNumber + 1
    const result = await sendGetPriorExpenses(() => getPriorExpenses(priorExpenseFilter, nextPageNumber))

    setPriorExpensesPaged({
      ...result,
      items: [...priorExpensesPaged.items, ...result.items],
    })
    setCurrentQueueNumber(nextPageNumber)
    setHasMoreQueueItems(result.pageCount > nextPageNumber)
  }
  const onSearch = (value: string) => {
    setPriorExpenseFilter({
      ...priorExpenseFilter,
      searchValue: value,
    })
  }
  const handleSort = (value: string) => {
    if (priorExpenseFilter.orderBy === value) {
      if (priorExpenseFilter.orderDescending) {
        setPriorExpenseFilter({
          ...priorExpenseFilter,
          orderDescending: false,
          orderBy: '',
        })
      } else {
        setPriorExpenseFilter({
          ...priorExpenseFilter,
          orderDescending: true,
          orderBy: value,
        })
      }
    } else {
      setPriorExpenseFilter({ ...priorExpenseFilter, orderDescending: false, orderBy: value })
    }
  }

  return (
    <Skeleton loading={queueLoading} active paragraph={{ rows: 4, className: 'p-24' }} title={{ className: 'ml-24' }}>
      <div className="prior_expenses_header">
        <h6>{translate('doc-review-dashboard.prior-expenses.title')}</h6>
      </div>
      <div className="mt-16 mr-24">
        <Row>
          <SimpleSearch
            placeholder="input search text"
            allowClear
            onSearch={onSearch}
            defaultValue={priorExpenseFilter.searchValue}
          />
        </Row>
      </div>
      <div className="mt-16 mb-16 mr-24">
        <SimpleSelect
          items={sortingOptions.map((sortingOption) => ({
            id: sortingOption.key,
            name: (
              <>
                {sortingOption.value}{' '}
                {priorExpenseFilter.orderBy === sortingOption.key && priorExpenseFilter.orderDescending && (
                  <i className="fas fa-arrow-down" style={{ display: 'inline' }}></i>
                )}
                {priorExpenseFilter.orderBy === sortingOption.key && !priorExpenseFilter.orderDescending && (
                  <i className="fas fa-arrow-up" style={{ display: 'inline' }}></i>
                )}
              </>
            ),
          }))}
          value={priorExpenseFilter.orderBy !== '' ? priorExpenseFilter.orderBy : undefined}
          placeholder={'Sort by...'}
          onSelect={(value) => handleSort(value)}
        />
      </div>
      <div className="prior_expenses mr-24" ref={parentScrollDiv}>
        <InfiniteScroll
          pageStart={0}
          loadMore={() => onPriorExpensesEndReach()}
          hasMore={hasMoreQueueItems}
          useWindow={false}
          loader={
            isPriorExpensesPagedLoading ? (
              <div className="loader" key={0}>
                {translate('general.loading')}
              </div>
            ) : (
              <></>
            )
          }
          getScrollParent={() => parentScrollDiv.current}
        >
          <>
            {priorExpensesPaged &&
              priorExpensesPaged.items.map((priorExpense) => (
                <PriorExpenseItem {...priorExpense} onPriorExpenseClick={props.onPriorExpenseClick} />
              ))}
          </>
        </InfiniteScroll>
      </div>
    </Skeleton>
  )
}

export default PriorExpenses
